import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  useTheme,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  TablePagination,
} from '@mui/material';
import FuelTypeFormModel from '../../components/AddNewFuelTypePopUp';
import FuelSourceFormModel from '../../components/AddNewFuelSourcePopUp';
import { Delete, Edit, LocalGasStation } from '@mui/icons-material';
import { FuelSource, FuelType, Meta } from '../../types';
import { fuelApi } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { extractApiError, unwrapListResponse, unwrapMetaResponse } from '../../apiUnwrapper/apiUnwrapper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const FuelTypesandSourcesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageFuel = user?.roleName === 'Admin';
  const [tabIndex, setTabIndex] = useState(0);
  const [fuelTypes, setFuelTypes] = useState<FuelType[]>([]);
  const [fuelSources, setFuelSources] = useState<FuelSource[]>([]);
  const [selectedFuelType, setSelectedFuelType] = useState<FuelType | undefined>(undefined);
  const [selectedFuelSource, setSelectedFuelSource] = useState<FuelSource | undefined>(undefined);
  const [isEditFuelType, setIsEditFuelType] = useState(false);
  const [isEditFuelSource, setIsEditFuelSource] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState("Are you sure?");
  const { enqueueSnackbar } = useSnackbar();
  const [fuelTypeMeta, setFuelTypeMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
  const [fuelSourceMeta, setFuelSourceMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });

  useEffect(() => {
    // Fetch fuel types and sources from API or state management
    // For now, we will use empty arrays
    loadFuelTypes(fuelTypeMeta.page, fuelTypeMeta.size);
    loadFuelSources(fuelSourceMeta.page, fuelSourceMeta.size);
  }, []);

  const loadFuelTypes = async (page = 1, size = 100) => {
    const types = await fuelApi.getFuelTypes(page, size);
    setFuelTypes(unwrapListResponse<FuelType>(types));
    setFuelTypeMeta(unwrapMetaResponse<Meta>(types) || { total: 0, size: 5, page: 1 });
  };

  const loadFuelSources = async (page = 1, size = 100) => {
    const sources = await fuelApi.getFuelSources(page, size);
    setFuelSources(unwrapListResponse<FuelSource>(sources));
    setFuelSourceMeta(unwrapMetaResponse<Meta>(sources) || { total: 0, size: 5, page: 1 });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const [openFT, setFTOpen] = useState(false);
  const [openFS, setFSOpen] = useState(false);
  
    const handleFTSubmit = async (fuelType: FuelType) => {
      try{
        const ft = await fuelApi.createUpdateFuelType(fuelType);
        setFTOpen(false);
        loadFuelTypes(fuelTypeMeta.page, fuelTypeMeta.size);
        enqueueSnackbar('Fuel type saved successfully', { variant: 'success' });
      }
      catch(error){
        console.error('Error saving fuel type:', error);
        const err  = extractApiError(error);
        enqueueSnackbar('Failed to save Fuel type: '+ err.message, { variant: 'error' });
      }
    };

    const handleFSSubmit = async (fuelSource: FuelSource) => {
      try{
        const fs = await fuelApi.createUpdateFuelSource(fuelSource);
        setFSOpen(false);
        loadFuelSources(fuelSourceMeta.page, fuelSourceMeta.size);
        enqueueSnackbar('Fuel source saved successfully', { variant: 'success' });
      }
      catch(error){
        console.error('Error saving fuel source:', error);
        const err  = extractApiError(error);
        enqueueSnackbar('Failed to save Fuel source: '+ err.message, { variant: 'error' });
      }
    };

    const handleFTDelete = (id: number) => {
      try{
        setConfirmAction(() => async () => {
        await fuelApi.deleteFuelType(id);
        loadFuelTypes(fuelTypeMeta.page, fuelTypeMeta.size);
        enqueueSnackbar('Fuel type deleted successfully', { variant: 'success' });
      });
      setConfirmMessage("Are you sure you want to delete this fuel type?");
      setOpenConfirm(true);
      }catch(error){
        console.error('Error deleting fuel type:', error);
        enqueueSnackbar('Error deleting fuel type: '+error, { variant: 'error' });
      }
    };

    const handleFSDelete = (id: number) => {
      try{
        setConfirmAction(() => async () => {
        await fuelApi.deleteFuelSource(id);
        loadFuelSources(fuelSourceMeta.page, fuelSourceMeta.size);
      });
      setConfirmMessage("Are you sure you want to delete this fuel source?");
      setOpenConfirm(true);
      }catch(error){
        console.error('Error deleting fuel source:', error);
        enqueueSnackbar('Error deleting fuel source: '+error, { variant: 'error' });
      }
    };


  const renderFuelTypeGrid = () => (
    <Box sx={{ mt: 3, mr:2, ml:2 }}>
      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, mb: 3 }}>
      <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {canManageFuel && <>
        <Button variant="contained" sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                 mt:1
        }} onClick={() => { setSelectedFuelType(undefined);
                            setIsEditFuelType(false);
                            setFTOpen(true)
                          }}>Add New Fuel Type</Button>
            <FuelTypeFormModel openFT={openFT} onClose={() => {setSelectedFuelType(undefined); setIsEditFuelType(false) ;setFTOpen(false);}}
                                       onSubmit={handleFTSubmit} fuelTypeData={selectedFuelType} isEditMode={isEditFuelType} />
        </>}
      <TablePagination
        component="div"
        count={fuelTypeMeta.total}
        page={fuelTypeMeta.page - 1}
        onPageChange={(e, newPage) => loadFuelTypes(newPage + 1, fuelTypeMeta.size)}
        rowsPerPage={fuelTypeMeta.size}
        onRowsPerPageChange={(e) => loadFuelTypes(1, parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 15, 20]}
        labelRowsPerPage={`Fuel Types per page`}
      />
      </Box>
      <TableContainer
                    component={Paper}
                    sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#2d3e50' }}>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Unit</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Cost Per Unit ($)</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Sort Order</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Active</TableCell>
                          {canManageFuel && <TableCell sx={{ color: theme.palette.text.primary }} align="center">
                            Actions
                          </TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fuelTypes.map((ft) => (
                          <TableRow key={ft.id}>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {ft.name}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {ft.unit}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {ft.costPerUnit}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {ft.sortOrder}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {ft.isActive ? (
                                <Chip label='Active' size="small" />
                              ) : (
                                <Chip label='Inactive' size="small" />
                              )} 
                            </TableCell>
                            {canManageFuel && <TableCell align="center">
                              {(
                                <>
                                  <IconButton
                                    sx={{ color: '#06beb6' }}
                                    onClick={() => {
                                      setSelectedFuelType(ft);
                                      setIsEditFuelType(true);
                                      setFTOpen(true);
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    sx={{ color: '#ef4444' }}
                                    onClick={() => handleFTDelete(ft.id)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </>
                              )}
                            </TableCell>}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
       </TableContainer>
       </CardContent>
       </Card>
    </Box>
  );

  const renderFuelSourceGrid = () => (
    <Box sx={{ mt: 3, mr:2, ml:2 }}>
      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, mb: 3 }}>
        <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {canManageFuel && <>
        <Button variant="contained" sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                 mt:1
        }} onClick={() => {
                           setSelectedFuelSource(undefined);
                           setIsEditFuelSource(false);
                           setFSOpen(true);
                          }} >Add New Fuel Source</Button>
            <FuelSourceFormModel openFS={openFS} onClose={() => { setSelectedFuelSource(undefined);
                                                                  setIsEditFuelSource(false);
                                                                  setFSOpen(false)}} 
                                                                  onSubmit={handleFSSubmit} 
                                                                  fuelSource = {selectedFuelSource} 
                                                                  isEditMode = {isEditFuelSource} />
        </>}
      <TablePagination
        component="div"
        count={fuelSourceMeta.total}
        page={fuelSourceMeta.page - 1}
        onPageChange={(e, newPage) => loadFuelSources(newPage + 1, fuelSourceMeta.size)}
        rowsPerPage={fuelSourceMeta.size}
        onRowsPerPageChange={(e) => loadFuelSources(1, parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 15, 20]}
        labelRowsPerPage={`Fuel Sources per page`}
      />
      </Box>
      <TableContainer
                    component={Paper}
                    sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#2d3e50' }}>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Location</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Latitude</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Longitude</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Sort Order</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>Active</TableCell>
                          {canManageFuel && <TableCell sx={{ color: theme.palette.text.primary }} align="center">
                            Actions
                          </TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fuelSources.map((fs) => (
                          <TableRow key={fs.id}>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {fs.name}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {fs.location}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {fs.latitude}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {fs.longitude}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {fs.sortOrder}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {fs.isActive ? (
                                <Chip label='Active' size="small" />
                              ) : (
                                <Chip label='Inactive' size="small" />
                              )} 
                            </TableCell>
                            {canManageFuel && <TableCell align="center">
                              {(
                                <>
                                  <IconButton
                                    sx={{ color: '#06beb6' }}
                                    onClick={() => {
                                      setSelectedFuelSource(fs);
                                      setIsEditFuelSource(true);
                                      setFSOpen(true);
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    sx={{ color: '#ef4444' }}
                                    onClick={() => handleFSDelete(fs.id)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </>
                              )}
                            </TableCell>}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
       </TableContainer>
       </CardContent>
       </Card>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
        <PageHeader
              title="Fuel Management"
              Icon={LocalGasStation}
              onBack={() => navigate('/')}
              backLabel="Back to Dashboard"
              subtitle="Fuel Types and Sources configuration"
            />

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        textColor="inherit"
        indicatorColor="secondary"
        sx={{
            mt: 2, mr:2, ml:2,
            display: 'flex',
            backgroundColor: theme.palette.background.default
        }}
        >
        <Tab
            label="Fuel Types"
            sx={{
            flex: 1,
            color: theme.palette.text.primary,
            textTransform: 'none',
            fontWeight: 'normal',
            '&.Mui-selected': {
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                fontWeight: 'bold',
                borderRadius: 1
            },
            '&.Mui-focusVisible': {
                outline: '2px solid #667eea',
                outlineOffset: '2px'
            },
            '&:hover': {
                background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)'
            }
            }}
        />
        <Tab
            label="Fuel Sources"
            sx={{
            flex: 1,
            color: theme.palette.text.primary,
            textTransform: 'none',
            fontWeight: 'normal',
            '&.Mui-selected': {
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                fontWeight: 'bold',
                borderRadius: 1
            },
            '&.Mui-focusVisible': {
                outline: '2px solid #667eea',
                outlineOffset: '2px'
            },
            '&:hover': {
                background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)'
            }
            }}
        />
        </Tabs>
        {canManageFuel && <ConfirmDialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={confirmAction}
          title="Confirm Delete"
          message={confirmMessage}
        />}


      {tabIndex === 0 && renderFuelTypeGrid()}
      {tabIndex === 1 && renderFuelSourceGrid()}
      </Box>
    </Box>
  );
};

export default FuelTypesandSourcesPage;